import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { secureGet, secureDelete } from "../../config/api";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import OptionsMenu from "../../components/OptionsMenu";
import Filter from "../../components/Filter";
import PageScroller from "../../components/PageScroller";
import LoadingLogo from "../../components/LoadingLogo";
import SnackbarNotification from "../../components/SnackbarNotification";
import DeletePopup from "../../components/DeletePopup";
import ModuleWrapper from "../../components/ModuleWrapper";
import { PrimaryButton } from "../../components/PrimaryButton";
import GenericCard from "../Users/GenericCard";

import AddIcon from "../../icons/plusB.svg";
import EditIcon from "../../icons/edit.svg";
import DeleteIcon from "../../icons/trashR.svg";

const Depots = () => {
  const { t } = useTranslation();

  const [repositories, setRepositories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ needle: "" });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [showModule, setShowModule] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);

  const topRef = useRef(null);
  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const showSnackbar = (message, severity = "success", manual = false) =>
    setSnackbar({ open: true, message, severity, manual });

  const filterOptions = [
    { name: "needle", type: "search" },
  ];

  const getOptions = (repo) => [
    { label: t("actions.modify"), icon: EditIcon, action: () => handleEdit(repo) },
    { label: t("actions.delete"), color: "#EF476F", icon: DeleteIcon, action: () => handleDelete(repo) },
  ];

  const fetchPaginatedRepos = useCallback(
    async (page = 0, size = pageSize, activeFilters = filters) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (activeFilters?.needle) params.append("needle", activeFilters.needle);
        params.set("page", page.toString());
        params.set("size", size.toString());
        const resp = await secureGet(`/repos?${params.toString()}`, showSnackbar, { signal });
        const { content = [], totalPages: tp = 1 } = resp?.data || {};
        setRepositories(content);
        setTotalPages(tp);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching repositories:", error);
        }
      } finally {
        setLoading(false);
      }
    },
    [filters, pageSize, signal]
  );

  useEffect(() => {
    fetchPaginatedRepos(0, pageSize, filters);
    return () => abortController.abort();
  }, []);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaginatedRepos(page, pageSize, filters);
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    fetchPaginatedRepos(0, size, filters);
  };

  const handleFilterChange = (filterName, filterValue) => {
    const updated = { ...filters, [filterName]: filterValue };
    setFilters(updated);
    setCurrentPage(0);
    fetchPaginatedRepos(0, pageSize, updated);
  };

  const resetFilters = () => {
    const defaults = { needle: "" };
    setFilters(defaults);
    setCurrentPage(0);
    fetchPaginatedRepos(0, pageSize, defaults);
  };

  const handleAddClick = () => {
    setSelectedRepo(null);
    setShowModule(true);
  };

  const handleEdit = (repo) => {
    setSelectedRepo(repo);
    setShowModule(true);
  };

  const handleDelete = (repo) => {
    setItemToDelete(repo);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      await secureDelete(`/repos/${itemToDelete.id}`, showSnackbar);
      fetchPaginatedRepos(currentPage, pageSize, filters);
      showSnackbar(t("confirmation.repoDelete"), "success");
    } catch (error) {
      console.error("Error deleting dépôt:", error);
    } finally {
      setDeletePopupVisible(false);
    }
  };

  const handleModuleClose = (action = "cancel") => {
    if (action === "submit") fetchPaginatedRepos(currentPage, pageSize, filters);
    setShowModule(false);
    setSelectedRepo(null);
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.repositories")}</title>
      </Helmet>

      {/* Header */}
      <div className="flex items-center mb-[15px]">
        <h1 className="text-[40px] font-semibold">{t("titles.repositories")}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t("primaryButtons.add_repo")}
            icon={AddIcon}
            onClick={handleAddClick}
          />
        </div>
      </div>

      {/* Filter */}
      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      {/* Content */}
      <div style={{ position: "relative", minHeight: "200px" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255,255,255,0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <LoadingLogo />
          </div>
        )}

        <div className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {!loading && repositories.length > 0 ? (
            repositories.map((repo) => (
              <GenericCard
                key={repo.id}
                title={repo.name || "Nom non disponible"}
                optionsMenu={
                  <div onClick={(e) => e.stopPropagation()}>
                    <OptionsMenu options={getOptions(repo)} />
                  </div>
                }
              />
            ))
          ) : (
            !loading && (
              <div className="col-span-full text-center mt-4">
              </div>
            )
          )}
        </div>
      </div>

      {/* Pagination */}
      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Module Wrapper */}
      {showModule && (
        <ModuleWrapper
          moduleType="repos"
          mode={selectedRepo ? "modify" : "add"}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, "success")}
          initialValues={selectedRepo || {}}
        />
      )}

      {/* Delete popup */}
      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {/* Snackbar */}
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Depots;
